"use client"

import React, {useState, useRef, useEffect, useCallback} from "react"
import {Button} from "@/components/ui/button"
import {Card, CardContent} from "@/components/ui/card"
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip"
import {ChevronLeft, ChevronRight, FolderOpen, X} from "lucide-react"
import LogoHeader from "../header/logo-header"
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf.mjs';
import { logger } from "@/lib/logger";
import { splitPdfToPages } from "../util/pdf-split";
import { MAX_UPLOAD_BYTES, planOnePagePdfOcr, formatMegabytes } from "../util/upload-limits";
import { setJobContext } from "@/lib/job-context";

const PDFJS_CDN_FALLBACK_VERSION = '4.10.38';

/** Scale used when rasterising PDF pages for the preview. */
const PDF_RENDER_SCALE = 2.0;

/**
 * The format rendered PDF pages are actually encoded in, and the extension that
 * goes with it.
 *
 * Both, together, and from one place. The canvas has always emitted JPEG while
 * the File was built as image/png with a .png name, and it worked only because
 * sharp sniffs the real format rather than trusting the label - so anything that
 * did trust it would have been wrong. The format was stated in three separate
 * places, which is how the label and the bytes drifted apart; now it is stated
 * once. DYS-021.
 */
const PDF_PAGE_IMAGE_TYPE = 'image/jpeg';
const PDF_PAGE_IMAGE_EXTENSION = 'jpg';
const PDF_PAGE_IMAGE_QUALITY = 0.85;

function configurePdfWorker(): void {
    try {
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
            'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
            import.meta.url
        ).toString();
        logger.debug('Configured PDF.js worker source', { workerSrc: pdfjs.GlobalWorkerOptions.workerSrc });
        return;
    } catch {
        // Bundled worker unavailable — fall back to CDN.
    }

    const pdfjsVersion =
        typeof pdfjs.version === 'string' && pdfjs.version.trim().length > 0
            ? pdfjs.version
            : PDFJS_CDN_FALLBACK_VERSION;

    if (pdfjsVersion === PDFJS_CDN_FALLBACK_VERSION) {
        logger.warn('PDF.js version unavailable, using pinned CDN fallback', {
            reportedVersion: pdfjs.version,
            fallbackVersion: PDFJS_CDN_FALLBACK_VERSION,
        });
    }

    pdfjs.GlobalWorkerOptions.workerSrc =
        `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/legacy/build/pdf.worker.min.mjs`;
    logger.debug('Configured PDF.js worker source', { workerSrc: pdfjs.GlobalWorkerOptions.workerSrc });
}

configurePdfWorker();

interface PdfViewProps {
    pdfFile: File
    onBackToHome: () => void
    onPdfOcrToText: (pages: File[]) => void
    onPdfDirectToText?: (pdfFile: File) => void
    onNavigateToHome: () => void
    onChangeFile?: (file: File) => void  // Replace current PDF or switch to image
    onError?: (message: string) => void   // Error handling
}

interface PageSourceMeta {
    sourceFile: File
    sourcePageIndex: number
    sourcePageCount: number
}

export default function PdfView({
    pdfFile, 
    onBackToHome, 
    onPdfOcrToText,
    onPdfDirectToText,
    onNavigateToHome,
    onChangeFile,
    onError
}: PdfViewProps) {
    const [backgroundColor] = useState("#FAF9F7")
    const [textColor] = useState("#4E5562")
    const [fontFamily] = useState("Verdana, sans-serif")
    const [fontSize] = useState(18)
    const [lineSpacing] = useState(1.5)

    // State for flattened page collection from multiple PDFs
    const [pageImages, setPageImages] = useState<string[]>([]);
    const [pageSources, setPageSources] = useState<PageSourceMeta[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loadingStatus, setLoadingStatus] = useState<"idle" | "loading" | "adding" | "ready" | "error">("idle");
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isProcessingOcr, setIsProcessingOcr] = useState(false); // Loading state for OCR button

    // File input refs
    const fileInputRef = useRef<HTMLInputElement>(null);        // For "Change PDF"
    const addMoreFileInputRef = useRef<HTMLInputElement>(null); // For "Add more"

    // Helper function to render PDF pages to images
    const renderPdfToImages = async (file: File, onProgress?: (progress: number) => void): Promise<string[]> => {
        const fileUrl = URL.createObjectURL(file);
        try {
            const pdf = await pdfjs.getDocument(fileUrl).promise;
            const images: string[] = [];
            
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                
                if (!context) continue;
                
                const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
                canvas.height = viewport.height;
                canvas.width = viewport.width;
                
                await page.render({ 
                    canvasContext: context, 
                    viewport
                }).promise;
                images.push(canvas.toDataURL(PDF_PAGE_IMAGE_TYPE, PDF_PAGE_IMAGE_QUALITY));
                
                if (onProgress) {
                    onProgress((i / pdf.numPages) * 100);
                }
            }
            
            return images;
        } finally {
            URL.revokeObjectURL(fileUrl);
        }
    };

    // Load initial PDF
    const loadPdf = useCallback(async () => {
        if (!pdfFile) return;
        
        setLoadingStatus("loading");
        setLoadingProgress(0);
        setCurrentPage(0);
        
        try {
            logger.info("Loading PDF file", { fileName: pdfFile.name, fileSize: pdfFile.size });
            
            const images = await renderPdfToImages(pdfFile, setLoadingProgress);
            
            setPageImages(images);
            setPageSources(images.map((_, index) => ({
                sourceFile: pdfFile,
                sourcePageIndex: index,
                sourcePageCount: images.length,
            })));
            setTotalPages(images.length);
            setLoadingStatus("ready");
            setJobContext({
                stage: 'preview',
                fileCount: 1,
                totalBytes: pdfFile.size,
                fileType: pdfFile.type,
                pageCount: images.length,
                renderScale: PDF_RENDER_SCALE,
            });
            logger.info("PDF loaded successfully", { pageCount: images.length });
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            logger.error("PDF loading failed", { error: error instanceof Error ? error : errorMsg });
            setLoadingStatus("error");
        }
    }, [pdfFile]);

    useEffect(() => {
        loadPdf();
    }, [loadPdf]);

    // Page navigation functions
    const goToNextPage = () => {
        if (currentPage < pageImages.length - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPrevPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "ArrowRight" || event.key === "ArrowDown" || event.key === "PageDown") {
                event.preventDefault();
                goToNextPage();
            } else if (event.key === "ArrowLeft" || event.key === "ArrowUp" || event.key === "PageUp") {
                event.preventDefault();
                goToPrevPage();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [currentPage, pageImages.length]);

    // Handle adding more PDFs
    const handleAddMorePdf = async (file: File) => {
        if (!file.type.includes("pdf")) {
            onError?.("Proszę wybrać plik PDF");
            return;
        }

        const previousStatus = loadingStatus;
        setLoadingStatus("adding");
        setLoadingProgress(0);

        try {
            logger.info("Adding more PDF pages", { fileName: file.name, fileSize: file.size });
            
            const newImages = await renderPdfToImages(file, setLoadingProgress);
            
            // Append to existing collection
            setPageImages(prev => [...prev, ...newImages]);
            setPageSources(prev => [
                ...prev,
                ...newImages.map((_, index) => ({
                    sourceFile: file,
                    sourcePageIndex: index,
                    sourcePageCount: newImages.length,
                })),
            ]);
            setTotalPages(prev => prev + newImages.length);
            
            // Navigate to the first page of the newly added PDF
            setCurrentPage(pageImages.length);
            
            setLoadingStatus("ready");
            logger.info("PDF pages added", { newPages: newImages.length, totalPages: pageImages.length + newImages.length });
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            logger.error("Error adding PDF", { error: error instanceof Error ? error : errorMsg });
            onError?.("Błąd podczas dodawania PDF");
            setLoadingStatus(previousStatus === "ready" ? "ready" : "error");
        }
    };

    // Handle page deletion
    const handleRemovePage = (index: number) => {
        const newImages = pageImages.filter((_, i) => i !== index);
        const newSources = pageSources.filter((_, i) => i !== index);

        if (newImages.length === 0) {
            setPageImages([]);
            setPageSources([]);
            setTotalPages(0);
            onBackToHome();
            return;
        }

        setPageImages(newImages);
        setPageSources(newSources);
        setTotalPages(newImages.length);

        if (index <= currentPage && currentPage > 0) {
            setCurrentPage(currentPage - 1);
        } else if (currentPage >= newImages.length) {
            setCurrentPage(newImages.length - 1);
        }
    };

    // Handle file selection for "Change PDF"
    const handleChangeFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            
            if (file.size > 10 * 1024 * 1024) {
                onError?.("Plik jest za duży. Maksymalny rozmiar to 10MB.");
                return;
            }

            if (!file.type.includes("image") && !file.type.includes("pdf")) {
                onError?.("Nieobsługiwany format pliku. Proszę wybrać obraz lub PDF.");
                return;
            }

            onChangeFile?.(file);
        }
        // Reset input value so the same file can be selected again
        e.target.value = '';
    };

    // Handle file selection for "Add more"
    const handleAddMoreFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            
            if (file.size > 10 * 1024 * 1024) {
                onError?.("Plik jest za duży. Maksymalny rozmiar to 10MB.");
                return;
            }

            handleAddMorePdf(file);
        }
        // Reset input value so the same file can be selected again
        e.target.value = '';
    };

    // Handle OCR extraction
    const handleOcrExtract = async () => {
        // Immediately show loading state
        setIsProcessingOcr(true);
        try {
            const isOnline = typeof navigator !== 'undefined' && navigator.onLine;
            const isSinglePage = pageImages.length === 1;
            const buildRenderedPageFiles = async () => {
                const pageFilePromises = pageImages.map((base64String, index) => {
                    return base64ToFile(base64String, `page-${index + 1}.${PDF_PAGE_IMAGE_EXTENSION}`);
                });
                const pageFiles = await Promise.all(pageFilePromises);
                const totalSize = pageFiles.reduce((sum, f) => sum + f.size, 0);
                logger.debug("Page files created for OCR", { fileCount: pageFiles.length, totalSize });
                return pageFiles;
            };
            const buildSelectedSplitPdfPages = async (): Promise<{
                pages: File[];
                missingIndices: number[];
            }> => {
                const sourceList = pageSources.length > 0
                    ? pageSources
                    : pageImages.map((_, index) => ({
                        sourceFile: pdfFile,
                        sourcePageIndex: index,
                        sourcePageCount: pageImages.length,
                    }));

                const indicesBySourceFile = new Map<File, Set<number>>();
                sourceList.forEach((sourcePage) => {
                    const pageIndexSet = indicesBySourceFile.get(sourcePage.sourceFile) ?? new Set<number>();
                    pageIndexSet.add(sourcePage.sourcePageIndex);
                    indicesBySourceFile.set(sourcePage.sourceFile, pageIndexSet);
                });

                const splitBySourceFile = new Map<File, Map<number, File>>();

                await Promise.all(Array.from(indicesBySourceFile.entries()).map(async ([sourceFile, pageIndexSet]) => {
                    const sortedPageIndices = Array.from(pageIndexSet.values()).sort((a, b) => a - b);
                    try {
                        const splitPages = await splitPdfToPages(sourceFile, sortedPageIndices);
                        const pageFileMap = new Map<number, File>();
                        splitPages.forEach((splitPage, idx) => {
                            pageFileMap.set(sortedPageIndices[idx], splitPage);
                        });
                        splitBySourceFile.set(sourceFile, pageFileMap);
                    } catch (error) {
                        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
                        logger.warn("Failed to split source PDF for OCR", {
                            sourceFileName: sourceFile.name,
                            requestedPageIndices: sortedPageIndices.join(','),
                            error: errorMsg,
                        });
                    }
                }));

                const selectedPages: File[] = [];
                const missingIndices: number[] = [];

                sourceList.forEach((sourcePage, visibleIndex) => {
                    const splitPages = splitBySourceFile.get(sourcePage.sourceFile);
                    const selectedPage = splitPages?.get(sourcePage.sourcePageIndex);

                    if (!selectedPage) {
                        logger.warn("Missing split page for source mapping, skipping", {
                            visibleIndex,
                            sourceFileName: sourcePage.sourceFile.name,
                            sourcePageIndex: sourcePage.sourcePageIndex,
                        });
                        missingIndices.push(visibleIndex);
                        return;
                    }

                    selectedPages.push(selectedPage);
                });

                return { pages: selectedPages, missingIndices };
            };

            if (isOnline && onPdfDirectToText && isSinglePage) {
                const singleSource = pageSources[0];
                const directSourceFile = singleSource?.sourceFile ?? pdfFile;
                const directSourcePageCount = singleSource?.sourcePageCount ?? pageImages.length;

                if (directSourcePageCount === 1) {
                    // The whole file goes up on this path - it is not split, because
                    // there is nothing to split. That makes it the one OCR path where
                    // the platform's request-body limit is reachable, and it is below
                    // the OCR route's own 10 MB check, so nothing else catches it.
                    if (planOnePagePdfOcr(directSourceFile.size, null) === 'pdf') {
                        logger.info("Starting direct PDF OCR (Gemini)", {
                            fileName: directSourceFile.name,
                            fileSize: directSourceFile.size,
                        });
                        setPageImages([]);
                        setPageSources([]);
                        onPdfDirectToText(directSourceFile);
                        return;
                    }

                    // Over the limit the direct upload cannot succeed, so send the
                    // rendered page instead of spending three attempts on a 413.
                    // Rasterising loses any text layer the PDF carried, which is why
                    // this is the fallback and not the default.
                    const renderedPageFiles = await buildRenderedPageFiles();
                    const renderedFile = renderedPageFiles[0];
                    const plan = planOnePagePdfOcr(
                        directSourceFile.size,
                        renderedFile ? renderedFile.size : null,
                    );

                    logger.warn("One-page PDF is over the upload limit", {
                        fileName: directSourceFile.name,
                        pdfSize: directSourceFile.size,
                        renderedSize: renderedFile ? renderedFile.size : null,
                        limit: MAX_UPLOAD_BYTES,
                        plan,
                    });

                    if (plan === 'too-large') {
                        // Refuse, and say why. Not the recognition message: nothing was
                        // ever read, and telling the user the text was unreadable sends
                        // them off retrying a file that cannot leave the device.
                        onError?.(
                            `Ten plik PDF jest za duży, aby go przesłać do rozpoznania `
                            + `(${formatMegabytes(directSourceFile.size)}, limit `
                            + `${formatMegabytes(MAX_UPLOAD_BYTES)}). Podziel dokument na `
                            + `mniejsze pliki PDF i spróbuj ponownie.`
                        );
                        return;
                    }

                    setPageImages([]);
                    setPageSources([]);
                    onPdfOcrToText(renderedPageFiles);
                    return;
                } else {
                    logger.info("Single visible page selected from multi-page PDF, using split OCR", {
                        fileName: directSourceFile.name,
                        sourcePageIndex: singleSource?.sourcePageIndex ?? 0,
                    });

                    const { pages: selectedSplitPages, missingIndices } = await buildSelectedSplitPdfPages();
                    if (selectedSplitPages.length !== 1) {
                        logger.warn("Failed to resolve single selected PDF page from split output, using rendered page", {
                            resolvedPageCount: selectedSplitPages.length,
                            missingIndices,
                        });
                        const renderedPageFiles = await buildRenderedPageFiles();
                        onPdfOcrToText(renderedPageFiles);
                        return;
                    }

                    setPageImages([]);
                    setPageSources([]);
                    onPdfOcrToText(selectedSplitPages);
                }
            } else if (isOnline) {
                logger.info("Starting per-page PDF OCR via split", {
                    pageCount: pageImages.length,
                });

                const { pages: splitPages, missingIndices } = await buildSelectedSplitPdfPages();

                if (splitPages.length === 0) {
                    logger.warn("Failed to resolve any split pages from source mapping, using rendered pages", {
                        missingIndices,
                    });
                    const renderedPageFiles = await buildRenderedPageFiles();
                    onPdfOcrToText(renderedPageFiles);
                    return;
                }

                if (missingIndices.length > 0) {
                    logger.warn("Some PDF pages could not be split, OCR will continue with the resolved subset", {
                        resolvedPageCount: splitPages.length,
                        missingPageCount: missingIndices.length,
                        missingIndices,
                    });
                }

                const totalSize = splitPages.reduce((sum, f) => sum + f.size, 0);
                logger.debug("PDF split completed for OCR", { fileCount: splitPages.length, totalSize });
                setPageImages([]);
                setPageSources([]);
                onPdfOcrToText(splitPages);
            } else {
                logger.info("Starting OCR extraction from rendered pages", {
                    pageCount: pageImages.length,
                    reason: "offline",
                });
                const renderedPageFiles = await buildRenderedPageFiles();
                setPageImages([]);
                setPageSources([]);
                onPdfOcrToText(renderedPageFiles);
            }
        } finally {
            // Reset loading state (though navigation will likely happen)
            setIsProcessingOcr(false);
        }
    };

    const base64ToFile = async (base64String: string, filename: string): Promise<File> => {
        const response = await fetch(base64String);
        const blob = await response.blob();
        return new File([blob], filename, {
            type: PDF_PAGE_IMAGE_TYPE,
            lastModified: Date.now()
        });
    };

    const handleRetry = () => {
        logger.info("Retrying PDF load");
        loadPdf();
    };

    const isLoading = loadingStatus === "loading" || loadingStatus === "adding";
    const isReady = loadingStatus === "ready";

    return (
        <div className="h-screen flex flex-col overflow-auto md:overflow-hidden" style={{backgroundColor: backgroundColor}}>
            <LogoHeader 
                onLogoClick={onNavigateToHome}
                onBackClick={onBackToHome}
                showBackButton={true}
                title="PDF"
            />

            <div className="flex-1 overflow-auto md:overflow-hidden pt-0 pb-0 sm:pb-0 sm:pt-0">
                <div className="h-full w-full max-w-4xl mx-auto px-4 pt-0 pb-0 md:pt-0 md:pb-4">
                    <Card className="w-full h-full flex flex-col border-none rounded-lg overflow-visible md:overflow-hidden shadow-md">
                        <TooltipProvider>
                            <CardContent
                                className="pt-4 pb-4 px-4 sm:pt-4 sm:pb-8 sm:px-8 text-center flex-1 flex flex-col min-h-0 relative overflow-visible md:overflow-hidden max-md:pb-0"
                                style={{
                                    color: textColor,
                                    fontSize: `${fontSize}px`,
                                    lineHeight: lineSpacing,
                                    fontFamily: fontFamily,
                                }}
                            >
                                {/* PDF Display Area */}
                                <div className="flex-1 relative min-h-0 overflow-hidden max-md:pb-36">
                                    {/* Loading state */}
                                    {isLoading && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 z-10">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#55366d] mb-4"></div>
                                            <p className="text-gray-600 mb-2">
                                                {loadingStatus === "adding" ? "Dodawanie stron..." : "Ładowanie PDF..."}
                                            </p>
                                            <div className="w-48 bg-gray-200 rounded-full h-2">
                                                <div 
                                                    className="bg-[#55366d] h-2 rounded-full transition-all duration-300" 
                                                    style={{ width: `${loadingProgress}%` }}
                                                ></div>
                                            </div>
                                            <span className="text-sm text-gray-500 mt-1">{Math.round(loadingProgress)}%</span>
                                        </div>
                                    )}

                                    {/* Error state */}
                                    {loadingStatus === "error" && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
                                            <p className="font-semibold mb-4 text-red-600">Błąd podczas ładowania PDF</p>
                                            <Button
                                                onClick={handleRetry}
                                                variant="outline"
                                                size="sm"
                                                className="text-red-600 border-red-600 hover:bg-red-50"
                                            >
                                                Spróbuj ponownie
                                            </Button>
                                        </div>
                                    )}

                                    {/* Page preview */}
                                    {isReady && pageImages.length > 0 && (
                                        <>
                                            <img
                                                src={pageImages[currentPage]}
                                                alt={`PDF Strona ${currentPage + 1}`}
                                                className="absolute inset-0 w-full h-full object-contain object-top"
                                            />
                                            {/* Delete button - only show when multiple pages */}
                                            {totalPages > 1 && (
                                                <button
                                                    onClick={() => handleRemovePage(currentPage)}
                                                    className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors z-10"
                                                    aria-label="Usuń stronę"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Desktop: Bottom toolbar with action buttons */}
                                <div className="hidden md:flex flex-col items-center mt-4 flex-shrink-0 gap-3">
                                    {/* Page navigation - styled with circular buttons and pill indicator */}
                                    {totalPages > 1 && (
                                        <div className="flex items-center gap-3 mb-2">
                                            <button
                                                onClick={goToPrevPage}
                                                disabled={currentPage === 0 || isLoading}
                                                className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                aria-label="Poprzednia strona"
                                            >
                                                <ChevronLeft className="h-5 w-5" />
                                            </button>
                                            <div className="flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-full px-4 py-2 shadow-sm">
                                                <span className="text-sm font-medium text-[#4E5562]">
                                                    Strona {currentPage + 1} z {totalPages}
                                                </span>
                                            </div>
                                            <button
                                                onClick={goToNextPage}
                                                disabled={currentPage === totalPages - 1 || isLoading}
                                                className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                aria-label="Następna strona"
                                            >
                                                <ChevronRight className="h-5 w-5" />
                                            </button>
                                        </div>
                                    )}

                                    {/* Action buttons with labels */}
                                    <div className="flex justify-center items-end gap-8">
                                        {/* Add more PDF */}
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button
                                                    onClick={() => addMoreFileInputRef.current?.click()}
                                                    className="flex flex-col items-center gap-1 text-[#55366d] hover:text-[#55366d]/70 transition-colors"
                                                    disabled={isLoading}
                                                >
                                                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M16 5H22" />
                                                        <path d="M19 2V8" />
                                                        <path d="M21 11.5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H12.5" />
                                                        <path d="M21 14.9997L17.914 11.9137C17.5389 11.5388 17.0303 11.3281 16.5 11.3281C15.9697 11.3281 15.4611 11.5388 15.086 11.9137L6 20.9997" />
                                                        <path d="M9 11C10.1046 11 11 10.1046 11 9C11 7.89543 10.1046 7 9 7C7.89543 7 7 7.89543 7 9C7 10.1046 7.89543 11 9 11Z" />
                                                    </svg>
                                                    <span className="text-[11px]">Dodaj</span>
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>Dodaj kolejny PDF</p>
                                            </TooltipContent>
                                        </Tooltip>

                                        {/* Change PDF - only show when single page */}
                                        {totalPages <= 1 && (
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className="flex flex-col items-center gap-1 text-[#55366d] hover:text-[#55366d]/70 transition-colors"
                                                        disabled={isLoading}
                                                    >
                                                        <FolderOpen className="h-5 w-5" />
                                                        <span className="text-[11px]">Zmień plik</span>
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>Wybierz inny plik</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        )}
                                    </div>

                                    {/* OCR Button */}
                                    <Button
                                        onClick={handleOcrExtract}
                                        className="bg-[rgba(85,54,109,1)] hover:bg-[rgba(85,54,109,0.9)] rounded-full text-white px-6 disabled:opacity-70"
                                        size="default"
                                        disabled={!isReady || pageImages.length === 0 || isProcessingOcr}
                                    >
                                        {isProcessingOcr ? (
                                            <>
                                                <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block"></span>
                                                Przetwarzanie...
                                            </>
                                        ) : (
                                            <>Rozpoznaj tekst{totalPages > 1 ? ` (${totalPages})` : ''}</>
                                        )}
                                    </Button>
                                </div>
                            </CardContent>
                        </TooltipProvider>
                    </Card>
                </div>
            </div>

            {/* Hidden file inputs */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,image/*"
                onChange={handleChangeFileInput}
                className="hidden"
                aria-label="Change file"
            />
            <input
                ref={addMoreFileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleAddMoreFileInput}
                className="hidden"
                aria-label="Add more PDF"
            />

            {/* Mobile: Fixed bottom toolbar */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm px-4 py-3 flex flex-col items-center gap-3 z-50">
                {/* Page navigation - styled with circular buttons and pill indicator */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between w-full max-w-sm">
                        <button
                            onClick={goToPrevPage}
                            disabled={currentPage === 0 || isLoading}
                            className="w-11 h-11 flex items-center justify-center bg-gray-200 active:bg-gray-300 rounded-full text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            aria-label="Poprzednia strona"
                        >
                            <ChevronLeft className="h-6 w-6" />
                        </button>
                        <div className="flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-full px-5 py-2.5 shadow-sm">
                            <span className="text-sm font-medium text-[#4E5562] whitespace-nowrap">
                                Strona {currentPage + 1} z {totalPages}
                            </span>
                        </div>
                        <button
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages - 1 || isLoading}
                            className="w-11 h-11 flex items-center justify-center bg-gray-200 active:bg-gray-300 rounded-full text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            aria-label="Następna strona"
                        >
                            <ChevronRight className="h-6 w-6" />
                        </button>
                    </div>
                )}

                {/* Action buttons row with labels */}
                <div className="flex justify-center items-end gap-8">
                    {/* Add more PDF */}
                    <button
                        onClick={() => addMoreFileInputRef.current?.click()}
                        className="flex flex-col items-center gap-0.5 text-[#55366d] active:text-[#55366d]/70"
                        disabled={isLoading}
                    >
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M16 5H22" />
                            <path d="M19 2V8" />
                            <path d="M21 11.5V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H12.5" />
                            <path d="M21 14.9997L17.914 11.9137C17.5389 11.5388 17.0303 11.3281 16.5 11.3281C15.9697 11.3281 15.4611 11.5388 15.086 11.9137L6 20.9997" />
                            <path d="M9 11C10.1046 11 11 10.1046 11 9C11 7.89543 10.1046 7 9 7C7.89543 7 7 7.89543 7 9C7 10.1046 7.89543 11 9 11Z" />
                        </svg>
                        <span className="text-[10px]">Dodaj</span>
                    </button>

                    {/* Change PDF - only show when single page */}
                    {totalPages <= 1 && (
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex flex-col items-center gap-0.5 text-[#55366d] active:text-[#55366d]/70"
                            disabled={isLoading}
                        >
                            <FolderOpen className="h-5 w-5" />
                            <span className="text-[10px]">Zmień plik</span>
                        </button>
                    )}
                </div>

                {/* Main OCR button */}
                <Button
                    onClick={handleOcrExtract}
                    className="bg-[rgba(85,54,109,1)] hover:bg-[rgba(85,54,109,0.9)] rounded-full text-white shadow-md px-6 disabled:opacity-70"
                    size="default"
                    disabled={!isReady || pageImages.length === 0 || isProcessingOcr}
                >
                    {isProcessingOcr ? (
                        <>
                            <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block"></span>
                            Przetwarzanie...
                        </>
                    ) : (
                        <>Rozpoznaj tekst{totalPages > 1 ? ` (${totalPages})` : ''}</>
                    )}
                </Button>
            </div>
        </div>
    )
}
